import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Select } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { Tooltip } from 'primeng/tooltip';
import { ConfirmationService, MessageService } from 'primeng/api';
import {
  ACTION_FILTER_TYPES,
  ALL_STATUSES,
  Activity,
  ActivityStatus,
  ActionTypeFilterValue,
  DEFAULT_FILTER_STATUSES,
  PRIMARY_STATUSES,
  ReviewGroupBy,
  STATUS_META,
  StatusFilterValue,
  actionCountDigit,
  actionCountLabel,
  actionPreviewTitles,
  actionTypeFilterLabel,
  activityHasActionType,
  confirmExecutingToast,
  dateGroupId,
  dateGroupLabel,
  dateGroupSortIndex,
  defaultFilterCount,
  formatStamp,
  relativeTime,
} from '../../models/activity.model';
import {
  ActivityInboxService,
  describeHttpError,
} from '../../services/activity-inbox.service';
import { ActivityDetailDialogComponent } from '../activity-detail-dialog/activity-detail-dialog.component';

/** Rows per page. The feed is already fully in memory, so paging is a slicing
 * concern rather than another round trip. */
const PAGE_SIZE = 10;

/** Which activities this browser has already opened, so freshly proposed work
 * can be marked as new. Versioned like the tab bar's pinned-tabs key so the
 * shape can change without tripping over stale entries. */
const SEEN_STORAGE_KEY = 'air.aiReview.seenActivityIds.v1';

/** Cap on remembered ids — without one this grows unbounded, and an id old
 * enough to fall off the list no longer needs a new/seen distinction. */
const SEEN_LIMIT = 500;

const STATUS_FILTER_OPTIONS: { label: string; value: StatusFilterValue }[] = [
  { label: 'Default', value: 'default' },
  ...ALL_STATUSES.map((status) => ({
    label: STATUS_META[status].label,
    value: status,
  })),
];

const ACTION_FILTER_OPTIONS: {
  label: string;
  value: ActionTypeFilterValue;
}[] = [
  { label: 'All', value: 'all' },
  ...ACTION_FILTER_TYPES.map((type) => ({
    label: actionTypeFilterLabel(type),
    value: type,
  })),
];

const GROUP_BY_OPTIONS: { label: string; value: ReviewGroupBy }[] = [
  { label: 'None', value: 'none' },
  { label: 'Date', value: 'date' },
];

/** One row of the Review list: a date heading, or an activity card. */
export type ReviewListRow =
  | { kind: 'group'; id: string; label: string }
  | { kind: 'activity'; activity: Activity };

function loadSeenIds(): Set<string> {
  try {
    const raw = localStorage.getItem(SEEN_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? new Set(parsed.filter((id): id is string => typeof id === 'string'))
      : new Set();
  } catch {
    // Private browsing or a corrupt entry: treat everything as already seen
    // rather than lighting up every row as new.
    return new Set();
  }
}

/**
 * The AI Activity Review queue: everything the conversation router agent has
 * proposed for one client, newest first, with a detail dialog per row for
 * confirming, retrying, or denying the proposed actions.
 *
 * Always scoped to a client and always cross-status — one request, because the
 * `by_client` GSI has no status in its key. The whole feed ends up local, so
 * filtering, counts, and paging are computed here rather than re-fetched.
 *
 * Mounted only by the FA client page, which wraps tab content in a
 * `bds-scroll-region`; the queue therefore flows to its content height and
 * lets the host scroll, rather than claiming a height and scrolling its own
 * list, which would nest two scroll containers and trap the inner one.
 */
@Component({
  selector: 'bn-review-queue',
  imports: [
    CommonModule,
    FormsModule,
    Button,
    ConfirmDialog,
    Select,
    SkeletonModule,
    Tooltip,
    ActivityDetailDialogComponent,
  ],
  providers: [ConfirmationService],
  templateUrl: './review-queue.component.html',
  styleUrl: './review-queue.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReviewQueueComponent {
  private readonly _inbox = inject(ActivityInboxService);
  private readonly _messages = inject(MessageService);
  private readonly _confirmation = inject(ConfirmationService);

  /** The client whose activities this shows. Required — the host renders the
   * tab only once a client is open, so there is no unscoped mode. */
  readonly clientGuid = input.required<string>();

  /** Display name for {@link clientGuid}, shown in place of the raw GUID. */
  readonly clientName = input<string | null>(null);

  /** Default-filter count after every successful load. A host showing its own
   * badge (the FA tab bar) would otherwise go stale the moment a reviewer
   * confirms or denies from the dialog. Not emitted on failure — the last
   * known answer beats inventing one from an error. */
  readonly defaultCountChange = output<number>();

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);

  private readonly _activities = signal<Activity[]>([]);
  private readonly _countsByStatus = signal<Record<ActivityStatus, number>>(
    {} as Record<ActivityStatus, number>,
  );
  private readonly _truncated = signal(false);
  private readonly _seenIds = signal<Set<string>>(loadSeenIds());

  /** Incremented per load so a superseded response can be discarded. */
  private _loadToken = 0;

  /** Landing filter is needs confirmation + error (see DEFAULT_FILTER_STATUSES). */
  readonly statusFilter = signal<StatusFilterValue>('default');
  readonly actionTypeFilter = signal<ActionTypeFilterValue>('all');
  readonly groupBy = signal<ReviewGroupBy>('none');
  readonly page = signal(0);
  /** Tooltip copy from GET /policy, overlaid on ACTION_TYPE_META. */
  readonly actionDescriptions = signal<Readonly<Record<string, string>>>({});

  readonly selectedActivity = signal<Activity | null>(null);
  readonly detailVisible = signal(false);
  /** Row currently posting confirm/deny, so both buttons disable together. */
  readonly busyActivityId = signal<string | null>(null);

  readonly statusFilterOptions = STATUS_FILTER_OPTIONS;
  readonly actionFilterOptions = ACTION_FILTER_OPTIONS;
  readonly groupByOptions = GROUP_BY_OPTIONS;
  readonly statusMeta = STATUS_META;

  readonly totalCount = computed(() => this._activities().length);

  readonly isDefaultFilter = computed(
    () => this.statusFilter() === 'default',
  );

  readonly activeStatuses = computed(() => {
    const selected = this.statusFilter();
    return selected === 'default'
      ? new Set(DEFAULT_FILTER_STATUSES)
      : new Set([selected]);
  });

  readonly filtered = computed(() => {
    const statuses = this.activeStatuses();
    const actionType = this.actionTypeFilter();
    return this._activities().filter((activity) => {
      if (!statuses.has(activity.status)) return false;
      if (actionType === 'all') return true;
      return activityHasActionType(activity, actionType);
    });
  });

  readonly pageCount = computed(() =>
    Math.max(1, Math.ceil(this.filtered().length / PAGE_SIZE)),
  );

  readonly pageNumbers = computed(() =>
    Array.from({ length: this.pageCount() }, (_, i) => i),
  );

  /** Filtered list, ordered for date grouping so headings stay together. */
  readonly ordered = computed(() => {
    const items = this.filtered();
    if (this.groupBy() === 'none') return items;
    return [...items].sort((left, right) => {
      const leftKey = dateGroupSortIndex(dateGroupId(left.created_at));
      const rightKey = dateGroupSortIndex(dateGroupId(right.created_at));
      if (leftKey !== rightKey) return leftKey - rightKey;
      return Date.parse(right.created_at) - Date.parse(left.created_at);
    });
  });

  readonly visible = computed(() => {
    const start = this.page() * PAGE_SIZE;
    return this.ordered().slice(start, start + PAGE_SIZE);
  });

  readonly visibleRows = computed((): ReviewListRow[] => {
    const items = this.visible();
    if (this.groupBy() === 'none') {
      return items.map((activity) => ({ kind: 'activity' as const, activity }));
    }
    const rows: ReviewListRow[] = [];
    let lastId: string | null = null;
    for (const activity of items) {
      const id = dateGroupId(activity.created_at);
      if (id !== lastId) {
        rows.push({ kind: 'group', id, label: dateGroupLabel(id) });
        lastId = id;
      }
      rows.push({ kind: 'activity', activity });
    }
    return rows;
  });

  /** "Showing 1–10 of 24", or empty when there's nothing to show. */
  readonly rangeLabel = computed(() => {
    const total = this.filtered().length;
    if (total === 0) return '';
    const start = this.page() * PAGE_SIZE + 1;
    return `Showing ${start}\u2013${Math.min(start + PAGE_SIZE - 1, total)} of ${total}`;
  });

  /** "12 of 24 activities" while filtered, "24 activities" when not. */
  readonly countLabel = computed(() => {
    const shown = this.filtered().length;
    const total = this.totalCount();
    const noun = total === 1 ? 'activity' : 'activities';
    return shown === total
      ? `${total} ${noun}`
      : `${shown} of ${total} ${noun}`;
  });

  /** "5 awaiting review · 1 executing · 1 failed" — only the actionable
   * statuses, and only those actually present. */
  readonly summaryLabel = computed(() => {
    const counts = this._countsByStatus();
    return PRIMARY_STATUSES.filter((status) => (counts[status] ?? 0) > 0)
      .map((status) => `${counts[status]} ${STATUS_META[status].summaryLabel}`)
      .join(' \u00b7 ');
  });

  /** Set when cursor pagination stopped before every page could be loaded, so
   * the counts above are floors rather than totals. */
  readonly countsCaveat = computed(() =>
    this._truncated()
      ? 'Some activities could not be loaded because paging stopped early; counts are a lower bound.'
      : null,
  );

  constructor() {
    // Loads on creation and again whenever the host switches clients. An
    // effect rather than ngOnInit because clientGuid is an input that changes
    // in place — the FA layout reuses one instance across client selections.
    effect(() => {
      const guid = this.clientGuid();
      this.page.set(0);
      void this._load(guid);
    });
  }

  async refresh(): Promise<void> {
    await this._load(this.clientGuid());
  }

  private async _load(clientGuid: string): Promise<void> {
    // Switching clients faster than the requests resolve would otherwise let
    // an earlier response land last and show the previous client's activities.
    const token = ++this._loadToken;
    this.loading.set(true);
    this.error.set(null);
    try {
      const [feed, policy] = await Promise.all([
        this._inbox.loadClientFeed(clientGuid),
        this._inbox.loadPolicy().catch(() => ({ descriptions: {} })),
      ]);
      if (token !== this._loadToken) return;
      this._activities.set(feed.activities);
      this._countsByStatus.set(feed.countsByStatus);
      this._truncated.set(feed.truncated);
      this.actionDescriptions.set(policy.descriptions ?? {});
      this._clampPage();
      this.defaultCountChange.emit(defaultFilterCount(feed.countsByStatus));
    } catch (err) {
      if (token !== this._loadToken) return;
      this.error.set(describeHttpError(err));
    } finally {
      if (token === this._loadToken) this.loading.set(false);
    }
  }

  isActive(status: ActivityStatus): boolean {
    // Default already covers needs confirmation + error. Lighting those as
    // selected in the Status dropdown as well made the control look mixed.
    return !this.isDefaultFilter() && this.activeStatuses().has(status);
  }

  selectDefault(): void {
    this.statusFilter.set('default');
    this.page.set(0);
  }

  selectStatus(status: ActivityStatus): void {
    this.statusFilter.set(status);
    this.page.set(0);
  }

  onStatusFilterChange(value: StatusFilterValue): void {
    this.statusFilter.set(value);
    this.page.set(0);
  }

  onActionTypeFilterChange(value: ActionTypeFilterValue): void {
    this.actionTypeFilter.set(value);
    this.page.set(0);
  }

  onGroupByChange(value: ReviewGroupBy): void {
    this.groupBy.set(value);
    this.page.set(0);
  }

  resetFilters(): void {
    this.statusFilter.set('default');
    this.actionTypeFilter.set('all');
    this.groupBy.set('none');
    this.page.set(0);
  }

  get hasFilters(): boolean {
    return (
      this.statusFilter() !== 'default' ||
      this.actionTypeFilter() !== 'all' ||
      this.groupBy() !== 'none'
    );
  }

  trackRow(row: ReviewListRow): string {
    return row.kind === 'group'
      ? `group:${row.id}`
      : `activity:${row.activity.activity_id}`;
  }

  goToPage(page: number): void {
    this.page.set(Math.min(Math.max(page, 0), this.pageCount() - 1));
  }

  isNew(activity: Activity): boolean {
    return !this._seenIds().has(activity.activity_id);
  }

  openActivity(activity: Activity): void {
    this._markSeen(activity.activity_id);
    this.selectedActivity.set(activity);
    this.detailVisible.set(true);
  }

  canActOnQueue(activity: Activity): boolean {
    return activity.status === 'needs_confirmation';
  }

  confirmFromRow(event: Event, activity: Activity): void {
    event.stopPropagation();
    event.preventDefault();
    void this._applyFromRow(activity, 'confirm');
  }

  denyFromRow(event: Event, activity: Activity): void {
    event.stopPropagation();
    event.preventDefault();
    this._confirmation.confirm({
      header: 'Deny this activity',
      message: "Denying is a terminal status change and can't be undone.",
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes, deny',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => void this._applyFromRow(activity, 'deny'),
    });
  }

  private async _applyFromRow(
    activity: Activity,
    action: 'confirm' | 'deny',
  ): Promise<void> {
    this.busyActivityId.set(activity.activity_id);
    try {
      await this._inbox.applyAction(activity.activity_id, action);
      this._messages.add({
        severity: 'success',
        summary:
          action === 'confirm'
            ? confirmExecutingToast(activity.title)
            : 'Activity denied',
        life: 2500,
      });
      await this.refresh();
    } catch (err) {
      this._messages.add({
        severity: 'error',
        summary: `Could not ${action} this activity`,
        detail: describeHttpError(err),
        life: 8000,
      });
    } finally {
      this.busyActivityId.set(null);
    }
  }

  /** The dialog mutated the activity (note, retry, deny), so the row's status,
   * notes, and proposal may all be stale. */
  async onActivityChanged(): Promise<void> {
    await this.refresh();
    const selected = this.selectedActivity();
    if (!selected) return;
    const fresh = this._activities().find(
      (activity) => activity.activity_id === selected.activity_id,
    );
    if (fresh) this.selectedActivity.set(fresh);
  }

  // Template helpers — thin wrappers so the markup stays declarative.
  actionTitlesFor = actionPreviewTitles;
  actionCountFor = actionCountDigit;
  actionCountTooltipFor = actionCountLabel;
  since = relativeTime;
  stamp = (iso: string) => formatStamp(iso, { seconds: true });

  private _clampPage(): void {
    if (this.page() > this.pageCount() - 1) this.page.set(0);
  }

  private _markSeen(activityId: string): void {
    if (this._seenIds().has(activityId)) return;
    const next = new Set(this._seenIds());
    next.add(activityId);
    // Newest ids are appended, so trimming from the front drops the oldest.
    const trimmed =
      next.size > SEEN_LIMIT
        ? new Set([...next].slice(next.size - SEEN_LIMIT))
        : next;
    this._seenIds.set(trimmed);
    try {
      localStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify([...trimmed]));
    } catch {
      // Storage unavailable — the in-memory set still works for this session.
    }
  }
}
